/* bysize-v4-size-gate.js — SIZE GATE for the By Size tools (Owen 2026-10-14, bands > 509 sq in).
 *
 * "Make certain tools not available at certain sizes — gray them out with a roll-over / tap
 * message." One shared module for BOTH engines (assets/bysize-v4.js and the live DTF fork
 * assets/nt-bgr-common.js): it only reads window.__bsv4Bridge (getState / subscribe / cfg),
 * which both expose, and never touches engine state except to switch precut off.
 *
 * A file is GATED when
 *   • file.bigFile — the upload exceeds imgix's render cap (pipeline.js applyRenderCap):
 *     the production file is the raw S3 original, so no imgix-served tool can apply; or
 *   • any of its rows is TALLER OR WIDER than cfg.limits.maxIn (22 in, the roll width — the
 *     size customers already know; Owen 2026-10-15: "22 inches height or width, our customers
 *     can't calculate sq inches"). Every size that existed before the bands fits in 22 x 22, so
 *     nothing changes for those; every new size has a side over 22; or
 *   • any row is larger than cfg.toolsGate.maxArea square inches, when that is set (Customizer:
 *     upload box → "Tools cutoff", product metafield custom.tools_max_area overrides). With the
 *     default 509 this never fires on its own (509 sq in always has a side over 22).
 *
 * Gated controls: Remove Background (toggle + options panel), Upscale, Edit with AI, Add Text,
 * Recolor, Filters (stage toolbar AND the dropdown variant), the DPI warning's "Upscale for Free"
 * button, and Design Notes (Owen 2026-10-15 "gray out notes": the stage tool + its popover
 * buttons, the band under the stage, the modal tile note — a note already typed is cleared so it
 * never ships on an oversized line). Precut and BG Color stay usable at every size (Owen
 * 2026-10-15). Greyed = .bsv4-size-gated (CSS in bysize-v4-size-gate.css);
 *
 * LABS FUNNEL BEACONS (Owen 2026-10-15: "how many people are uploading oversized sheets to
 * these pages and of that who sizes down before their add to cart"). This module already knows
 * the moment a design crosses 22 in on a side, so it reports it — Labs feature slug
 * `oversized-by-size`, the same POST /evt path AI Edits and Design Studio use (sendBeacon,
 * fire-and-forget, nothing awaited). Two TRACKS (Owen 2026-10-15 "two tracks, landed oversized
 * and landed under"), each once per file unless noted:
 *   track L — landed oversized:  ovs_landed (the measured upload is already over 22 on a side)
 *             → ovs_landed_down (went back under) → ovs_landed_atc_over / ovs_landed_atc_under
 *   track U — landed under, sized up:  ovs_sized_up (typed past 22)
 *             → ovs_up_down (went back under) → ovs_up_atc_over / ovs_up_atc_under
 *   adds count every add (v = sq in). The earlier un-tracked names (ovs_sized_down, ovs_atc_over,
 *   ovs_atc_under) are retired here but stay on the Labs allowlist so a cached copy never strays.
 * `ver` = dtf | uv | dtf-gang | uv-gang (the page). Every beacon call is wrapped so counting can
 * never stop the gate from marking.
 * BY SIZE FUNNEL (Owen 2026-10-20, the Labs "By Size" report — visits → uploads → the 2x2 of
 * landed under/over x added under/over): three more beacons, same slug, same `ver`:
 *   bs_visit      once per page load (the report's Visited tile)
 *   bs_upload     once per measured file, v = sq in (Uploaded; under = bs_upload − ovs_landed)
 *   bs_atc_under  on add, per file that landed under 22 and NEVER went over (the plain path;
 *                 a sized-up-then-down file is ovs_up_atc_under, as before)
 * the switch-off of an already-on Remove Background / Upscale waits SETTLE_MS and runs only if the
 * file is still gated (a transient over-22 value the engine clamps back must not flip BGR off);
 * every click / tap / keyboard activation inside is cancelled at the WINDOW capture phase (fires
 * before any document-level capture listener, e.g. the recolor engine's) and shows the message
 * in a real <div> tooltip (no pseudo-elements — see the Visual Verification Gate precedent).
 */
(function () {
  'use strict';
  if (window.__bsv4SizeGate) return;
  window.__bsv4SizeGate = true;

  var SEL = [
    '.bsv4-ai-card--bgr', '.bsv4-ai-card--up', '.bsv4-bgr',
    '[data-bsv4-edit-open]', '[data-bsv4-addtext]', '.nt-recolor-btn', '[data-bsv4-fx]', '[data-bsv4-upscale]',
    '[data-bsv4-aitools-trigger]',
    '[data-bsv4-notes]', '.bsv4-notes-fab', '.bsv4-cta__notes', '.bsv4-tile__note',
    // By Size v5 (Owen 2026-11-01 "all tools become unavailable at anything over 22"): Eraser, Crop, Edges, Undo/Redo,
    // Add a Note. Absent on every other page, so nothing changes there.
    '[data-bsv4-erase]', '[data-ntst-crop]', '[data-bsv4-edges-open]', '[data-ntst-undo]', '[data-ntst-redo]', '[data-ntre-open]'
  ].join(',');
  // v5 = the tool bar page. There, over 22 SUSPENDS the work instead of discarding it (Owen 2026-11-01: "keep their
  // history if they size back down so work with tools isn't lost"): what the gate switched off comes back on, and a
  // typed note comes back, once every row is back under the line. History is frozen while gated (no undo/redo).
  function isV5() { return !!window.__bsv5Tools; }
  var CLS = 'bsv4-size-gated';
  var B = null, cfg = null, root = null, tip = null, tipTimer = null, raf = 0;

  // ---- Labs funnel beacons (see header) ---------------------------------------------------
  var EVT_URL = 'https://uszaouez69.execute-api.us-east-1.amazonaws.com/evt';
  var SLUG = 'oversized-by-size';
  var track = {}; // fileId -> { landed, over, everOver, up, down, sqin }  (landed = track L, else track U)
  function labsEnv() { try { return window.Shopify && Shopify.theme && Shopify.theme.role === 'main' ? 'production' : 'staging'; } catch (e) { return 'staging'; } }
  function pageVariant() {
    try {
      var p = String(location.pathname || '');
      var gang = !!(cfg && cfg.gangSheet) || /gang/i.test(p);
      return (/uv/i.test(p) ? 'uv' : 'dtf') + (gang ? '-gang' : '');
    } catch (e) { return 'dtf'; }
  }
  function beacon(e, v) {
    try {
      var body = JSON.stringify({ f: SLUG, e: e, v: v, ver: pageVariant(), env: labsEnv() });
      if (navigator.sendBeacon) navigator.sendBeacon(EVT_URL, body);
      else fetch(EVT_URL, { method: 'POST', body: body, keepalive: true }).catch(function () {});
    } catch (x) { /* never break the gate */ }
  }
  // VISITED = SESSIONS, ONE stream for every Labs report (Owen 2026-10-21: "needs to be sessions for all
  // labs that reference visited, using the same feature"). Labs feature `visits`, event `visit`,
  // Variant `<product>:<page>` (dtf:bysize, uv:bysize, dtf:gang, uv:gang) — fired once per browser
  // session per page variant from THIS module, which every By Size and gang page loads. Reloads and
  // back-and-forward do not count again; a new tab is a new session (close to Shopify's own session);
  // a browser that refuses sessionStorage counts every load. The gang card's own `visit:page` and the
  // old `bs_visit` are retired — both reports read this stream.
  function visitBeacon() {
    try {
      var pv = pageVariant();
      var ver = (/^uv/.test(pv) ? 'uv' : 'dtf') + ':' + (/-gang$/.test(pv) ? 'gang' : 'bysize');
      var body = JSON.stringify({ f: 'visits', e: 'visit', ver: ver, env: labsEnv() });
      if (navigator.sendBeacon) navigator.sendBeacon(EVT_URL, body);
      else fetch(EVT_URL, { method: 'POST', body: body, keepalive: true }).catch(function () {});
    } catch (x) { /* never break the gate */ }
  }
  try {
    var vkey = 'labs:visited:' + pageVariant();
    if (!sessionStorage.getItem(vkey)) { sessionStorage.setItem(vkey, '1'); visitBeacon(); }
  } catch (e) { visitBeacon(); }
  // LANDING RECORD (Owen 2026-10-21, the Labs upload ledger): once per measured file, the file's own
  // key + page + measured size go to Labs as a stored row (not a metric) — the per-file identity that
  // lets Labs join this landing to the cart line and the order line that carry the same key. Same
  // fire-and-forget sendBeacon, text/plain (no preflight), wrapped so it can never touch the page.
  var LANDED_URL = 'https://labs-admin.ninjaondemand.com/api/landed';
  function fileKeyOf(file) {
    try {
      var u = String((file && (file.s3Url || file.imgixUrl)) || '');
      var base = u.split('?')[0].split('#')[0].split('/').pop() || '';
      return /____image_\d{13}_/.test(base) ? base.slice(0, 200) : '';
    } catch (e) { return ''; }
  }
  function landedRecord(file, sz, w, h) {
    try {
      var k = fileKeyOf(file); if (!k || !(w > 0 && h > 0)) return;
      var pv = pageVariant();
      var body = JSON.stringify({ k: k, p: /^uv/.test(pv) ? 'uv' : 'dtf', pg: /-gang$/.test(pv) ? 'gang' : 'bysize', w: Math.round(w * 100) / 100, h: Math.round(h * 100) / 100, env: labsEnv() });
      var blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon) navigator.sendBeacon(LANDED_URL, blob);
      else fetch(LANDED_URL, { method: 'POST', body: blob, keepalive: true }).catch(function () {});
    } catch (x) { /* never break the gate */ }
  }
  function maxDimOf() { return Number(cfg && cfg.limits && cfg.limits.maxIn) || 22; }
  // Largest row's sq in, and whether any row is over the max side.
  function sizeOf(file) {
    var rows = (file && file.rows) || []; var max = maxDimOf(); var over = false, sqin = 0, bw = 0, bh = 0;
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i]; if (!r) continue; var w = Number(r.w), h = Number(r.h);
      if (!(w > 0 && h > 0)) continue;
      if (Math.max(w, h) > max + EPS) over = true;
      if (w * h > sqin) { sqin = Math.round(w * h); bw = w; bh = h; }
    }
    // w/h = the largest row's size (= the measured size at landing, before any edit)
    return { over: over, sqin: sqin, w: bw, h: bh, measured: rows.length > 0 };
  }
  function trackFiles(files) {
    try {
      var live = {};
      files.forEach(function (f) {
        if (!f || !f.id) return; live[f.id] = 1;
        var sz = sizeOf(f); if (!sz.measured) return;
        var t = track[f.id];
        if (!t) {
          t = track[f.id] = { landed: sz.over, over: sz.over, everOver: sz.over, up: false, down: false, sqin: sz.sqin };
          beacon('bs_upload', sz.sqin); // By Size funnel: every measured upload, once
          // The ledger wants the file's NATURAL measured size (the engine's inW/inH), not the first row:
          // By Size seeds the first row to FIT 22 in, so a 22x24 upload lands as a 17.98x20 row and the
          // "landed over 22" signal would never fire. The rows still drive the gate itself.
          var nw = Number(f.inW), nh = Number(f.inH);
          landedRecord(f, sz, nw > 0 && nh > 0 ? nw : sz.w, nw > 0 && nh > 0 ? nh : sz.h);
          if (sz.over) beacon('ovs_landed', sz.sqin);
          return;
        }
        if (sz.over) t.sqin = sz.sqin;
        if (sz.over && !t.over) { t.over = true; t.everOver = true; if (!t.up && !t.landed) { t.up = true; beacon('ovs_sized_up', sz.sqin); } }
        else if (!sz.over && t.over) { t.over = false; if (!t.down) { t.down = true; beacon(t.landed ? 'ovs_landed_down' : 'ovs_up_down', sz.sqin); } }
      });
      Object.keys(track).forEach(function (id) { if (!live[id]) delete track[id]; });
    } catch (x) { /* never break the gate */ }
  }
  document.addEventListener('bsv4:added-to-cart', function () {
    try {
      Object.keys(track).forEach(function (id) {
        var t = track[id];
        var pre = t.landed ? 'ovs_landed_' : 'ovs_up_';
        if (t.over) beacon(pre + 'atc_over', t.sqin);
        else if (t.everOver) beacon(pre + 'atc_under', t.sqin);
        else beacon('bs_atc_under', t.sqin); // By Size funnel: landed under, never over, added under
      });
    } catch (x) { /* never break the gate */ }
  });

  function gateFor(file) {
    if (!file) return null;
    var tg = cfg.toolsGate || {};
    if (file.bigFile) return { kind: 'file', msg: tg.fileMsg || 'Not available for files this large' };
    var maxDim = Number(cfg.limits && cfg.limits.maxIn) || 22;
    var maxArea = Number(tg.maxArea);
    var rows = file.rows || [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i]; if (!r) continue;
      var w = Number(r.w), h = Number(r.h);
      if (Math.max(w, h) > maxDim + EPS || (maxArea > 0 && w * h > maxArea + 1e-6)) {
        return { kind: 'size', msg: String(tg.msg || 'Not available for designs over [dim] inches tall or wide')
          .replace(/\[dim\]|\{dim\}/g, maxDim).replace(/\[area\]|\{area\}/g, maxArea > 0 ? maxArea : Math.round(maxDim * maxDim)) };
      }
    }
    return null;
  }

  // v5: a file whose tools the gate holds is still gated while its size, at the HELD shape (same width, the shape it
  // has with the tools back on), would be over the line — the switch-back waits for a size that fits with the tools on
  function heldGate(file) {
    var re = file && reenable[file.id];
    if (!re || !(re.aspect > 0)) return null;
    var maxDim = Number(cfg.limits && cfg.limits.maxIn) || 22;
    var rows = file.rows || [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i]; if (!r) continue;
      var w = Number(r.w); if (!(w > 0)) continue;
      if (Math.max(w, w / re.aspect) > maxDim + EPS) {
        var tg = cfg.toolsGate || {};
        return { kind: 'size', msg: String(tg.msg || 'Not available for designs over [dim] inches tall or wide')
          .replace(/\[dim\]|\{dim\}/g, maxDim).replace(/\[area\]|\{area\}/g, Math.round(maxDim * maxDim)) };
      }
    }
    return null;
  }

  // By Size v5 (Owen 2026-11-02): a print-ready file (nt-print-ready.js) can't take Remove BG / Upscale — those controls
  // stay visible but greyed, with this hover message, through the same mark / tooltip / click guard as the size gate.
  var PR_SEL = '.bsv4-ai-card--bgr,.bsv4-ai-card--up,.bsv4-bgr,[data-bsv4-upscale],.bsv4-dpi-up';
  var PR_GATE = { kind: 'print-ready', msg: 'This feature is disabled for print ready files' };
  function prGate(f, el) {
    var pr = window.ntPrintReady;
    if (!isV5() || !f || !el || !pr || typeof pr.isPrintReady !== 'function' || !pr.isPrintReady(f.s3Url)) return null;
    return el.matches && el.matches(PR_SEL) ? PR_GATE : null;
  }
  function mark(el, g) {
    if (!el) return;
    if (g) { el.classList.add(CLS); el.setAttribute('data-bsv4-size-gate-msg', g.msg); el.setAttribute('aria-disabled', 'true'); }
    else if (el.classList.contains(CLS)) { el.classList.remove(CLS); el.removeAttribute('data-bsv4-size-gate-msg'); el.removeAttribute('aria-disabled'); }
  }

  function each(scope, fn) { Array.prototype.forEach.call(scope.querySelectorAll(SEL), fn); }

  // Design Notes (Owen 2026-10-15 "gray out notes"): a gated file must not ship a note. The page
  // band / stage drawer write properties[Design Notes] (cart.js reads it at add time) and the modal
  // tile keeps file.designNote in sync with its textarea + checkbox (modal.js) — so clear the
  // textareas, uncheck the tile box (change event lets modal.js clear file.designNote), close the
  // stage popover and drop the tool's "saved" tick. Done BEFORE marking so the capture guard does
  // not swallow the synthetic change.
  function clearNotes(scope, active) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bsv4-tile-note-open]:checked'), function (cb) {
      cb.checked = false; cb.dispatchEvent(new Event('change', { bubbles: true }));
    });
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bsv4-tile-note]'), function (ta) {
      if (ta.value) { ta.value = ''; ta.dispatchEvent(new Event('input', { bubbles: true })); }
    });
    if (!active) return;
    Array.prototype.forEach.call(root.querySelectorAll('.bsv4-cta__notes textarea, [data-bsv4-notes-ta]'), function (ta) {
      if (ta.value && isV5()) stashedNotes.push({ ta: ta, v: ta.value });
      ta.value = '';
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-bsv4-notes-panel]'), function (p) { p.hidden = true; });
    Array.prototype.forEach.call(root.querySelectorAll('[data-bsv4-notes], .bsv4-notes-fab'), function (b) { b.classList.remove('is-saved'); b.setAttribute('aria-expanded', 'false'); });
  }

  // "Not available" must also mean OFF: a file whose Remove Background / Upscale was already on
  // (the raster default) when it crossed the cutoff gets them switched off through the engine's
  // own toggles — clicked BEFORE the controls are marked, so the window-capture guard lets the
  // click through and bgr.js / edit.js run their normal off path (preview swap, picker, commit).
  // Switching Remove Background off makes the engine re-measure the file and RE-SEED its rows
  // to the default size (seen on the sandbox 2026-10-15: a typed 22 x 46 snapped back to
  // 6 x 12.67). So the typed sizes are remembered per file and, once the engine has re-seeded,
  // replayed through the width input exactly as typing would (sizing.js listens to input +
  // change on [data-bsv4-w]). One replay per switch-off, within 8 s.
  var restores = {};
  // v5 only: per file, the toggles the gate switched off ({bgr, sr, up}); turned back on when the file is under the
  // line again (settled, like the switch-off). Survives the customer's own typing — typing back down is the point.
  var reenable = {}, reenableTimers = {}, reenableHold = {}, stashedNotes = [];
  // v5 history (nt-stage-tools.js) asks this before recording a step: the gate's own switch-off / switch-on is never
  // an undo step. Held while over the line, while a switch-back is pending, and 4 s after it (Remove BG re-measures).
  window.__bsv4SizeGated = function (fileId) {
    try {
      if (reenable[fileId] || reenableTimers[fileId] || (reenableHold[fileId] && Date.now() < reenableHold[fileId])) return true;
      var f = (B.getState().files || []).find(function (x) { return x.id === fileId; });
      return !!(f && gateFor(f));
    } catch (x) { return false; }
  };
  // SETTLE before switching anything off (Owen 2026-10-15, seen on Will's arm): a typed value can
  // be over 22 for one state commit and then be clamped back by the engine (23 on a square design
  // snaps to 22 x 22). Marking is immediate and harmless; the switch-off changes engine state, so
  // it waits SETTLE_MS and runs only if the file is STILL gated. One timer per file.
  var SETTLE_MS = 1000;
  // Rounding tolerance (seen on Will's arm 2026-10-16): a typed 23 on a square design lands as
  // 22.01 x 22 for ~600 ms before the engine's second pass rounds it to 22 x 22. 22.01 is never
  // a real new size (its area is 484, far under the first new band), so anything within EPS of
  // the max side is treated as AT the limit, for the gate and for the funnel beacons alike.
  var EPS = 0.05;
  var settleTimers = {};
  function switchOffSettled(fileId) {
    if (settleTimers[fileId]) return;
    settleTimers[fileId] = setTimeout(function () {
      delete settleTimers[fileId];
      try {
        var st = B.getState(); var f = (st.files || []).find(function (x) { return x.id === fileId; });
        if (!f || !gateFor(f)) return; // clamped back under the line meanwhile — leave the toggles alone
        var hosts = root.querySelectorAll('[data-bsv4-bgr-for="' + fileId + '"],[data-bsv4-edit-for="' + fileId + '"],[data-bsv4-tile="' + fileId + '"]');
        Array.prototype.forEach.call(hosts, function (host) { switchOff(host, fileId); });
        var act = activeFile(st);
        if (act && act.id === fileId) Array.prototype.forEach.call(root.querySelectorAll('.bsv4-aitools--stage'), function (card) { switchOff(card, fileId); });
      } catch (x) { /* never break the gate */ }
    }, SETTLE_MS);
  }
  function switchOff(scope, fileId) {
    var sel = '[data-bsv4-toggle-bgr]:checked, [data-bsv4-toggle-sr]:checked, [data-bsv4-upscale][aria-pressed="true"]';
    var toggles = scope.querySelectorAll(sel);
    if (!toggles.length) return;
    if (isV5()) {
      var re = reenable[fileId] || (reenable[fileId] = {});
      // the design's shape WITH the held tools (Remove BG on): switching it off re-measures the bare upload, whose
      // shape differs — a tall cut-out on a square upload lands at 22 x 22 (under the line) and would look "back
      // under" while switching the tools back on puts it straight over again (Eraser session 11-01: endless loop)
      var r0 = (rowsFor(fileId) || [])[0];
      if (!re.aspect && r0 && r0.w > 0 && r0.h > 0) re.aspect = Number(r0.w) / Number(r0.h);
      Array.prototype.forEach.call(toggles, function (el) {
        if (el.matches('[data-bsv4-toggle-bgr]')) re.bgr = true;
        else if (el.matches('[data-bsv4-toggle-sr]')) re.sr = true;
        else re.up = true;
      });
    }
    var rows = rowsFor(fileId);
    if (rows.length && !restores[fileId]) restores[fileId] = { at: Date.now(), dims: rows.map(function (r) { return { w: Number(r.w), h: Number(r.h) }; }) };
    Array.prototype.forEach.call(toggles, function (el) {
      var g = el.closest('.' + CLS); if (g) g.classList.remove(CLS);
      try { el.click(); } catch (e) { /* never let a stuck toggle break the gate */ }
    });
  }
  function reenableSettled(fileId) {
    if (reenableTimers[fileId]) return;
    reenableTimers[fileId] = setTimeout(function () {
      delete reenableTimers[fileId];
      try {
        var st = B.getState(); var f = (st.files || []).find(function (x) { return x.id === fileId; });
        var re = reenable[fileId];
        if (!f) { delete reenable[fileId]; return; }
        if (!re || gateFor(f) || heldGate(f)) return; // still over the line (as it is, or with the tools back on) — keep waiting
        delete reenable[fileId];
        reenableHold[fileId] = Date.now() + 4000;
        // turning Remove BG back on re-measures + re-seeds the rows (to the default size); keep the size the customer
        // typed coming back under the line — the same replay the switch-off uses
        var rows = f.rows || [];
        if (rows.length) restores[fileId] = { at: Date.now(), dims: rows.map(function (r) { return { w: Number(r.w), h: Number(r.h) }; }) };
        var hosts = Array.prototype.slice.call(root.querySelectorAll('[data-bsv4-bgr-for="' + fileId + '"],[data-bsv4-edit-for="' + fileId + '"],[data-bsv4-tile="' + fileId + '"]'));
        var act = activeFile(st);
        if (act && act.id === fileId) hosts = hosts.concat(Array.prototype.slice.call(root.querySelectorAll('.bsv4-aitools--stage')));
        var done = {};
        hosts.forEach(function (host) {
          var pick = [];
          if (re.bgr && !done.bgr) pick.push(['bgr', '[data-bsv4-toggle-bgr]:not(:checked)']);
          if (re.sr && !done.sr) pick.push(['sr', '[data-bsv4-toggle-sr]:not(:checked)']);
          if (re.up && !done.up) pick.push(['up', '[data-bsv4-upscale]:not([aria-pressed="true"])']);
          pick.forEach(function (p) { var el = host.querySelector(p[1]); if (el) { done[p[0]] = true; try { el.click(); } catch (e) { /* never break the gate */ } } });
        });
      } catch (x) { /* never break the gate */ }
    }, SETTLE_MS);
  }
  function restoreNotes() {
    if (!stashedNotes.length) return;
    var list = stashedNotes; stashedNotes = [];
    list.forEach(function (n) { if (n.ta.isConnected && !n.ta.value) { n.ta.value = n.v; n.ta.dispatchEvent(new Event('input', { bubbles: true })); } });
  }
  function rowsFor(fileId) {
    var st = B.getState(); var f = (st.files || []).find(function (x) { return x.id === fileId; });
    return (f && f.rows) || [];
  }
  function widthInputsFor(fileId) {
    // page rows carry the file id on their row node; tiles on [data-bsv4-tile]
    var tile = root.querySelector('[data-bsv4-tile="' + fileId + '"]');
    var scope = tile || root;
    return Array.prototype.slice.call(scope.querySelectorAll('[data-bsv4-w]')).filter(function (i) { return i.offsetWidth || i.offsetHeight; });
  }
  // The customer's OWN typing after a switch-off must win (live 2026-10-16, stock engine: a
  // customer who crossed 22, then typed 7 within the 8 s window, was snapped back to 22 because
  // the replay took their edit for the engine's re-seed). Any real input/change on a size field
  // that the gate did not dispatch itself drops every pending restore.
  var replaying = false;
  ['input', 'change'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (replaying) return;
      var t = e.target;
      if (t && t.matches && t.matches('[data-bsv4-w],[data-bsv4-h]')) restores = {};
    }, true);
  });
  function replayRestores() {
    Object.keys(restores).forEach(function (fileId) {
      var r = restores[fileId];
      if (Date.now() - r.at > 8000) { delete restores[fileId]; return; }
      var rows = rowsFor(fileId); if (!rows.length) return;
      var changed = rows.some(function (row, i) { return r.dims[i] && Math.abs(Number(row.w) - r.dims[i].w) > 0.01; });
      if (!changed) return; // the engine has not re-seeded (yet)
      delete restores[fileId];
      var inputs = widthInputsFor(fileId);
      replaying = true;
      try {
        r.dims.forEach(function (d, i) {
          var input = inputs[i]; if (!input || !(d.w > 0)) return;
          input.value = String(d.w);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } finally { replaying = false; }
    });
  }

  function activeFile(st) {
    return (st.activeFileId && st.files.find(function (f) { return f.id === st.activeFileId; })) || st.files[0] || null;
  }

  function apply() {
    raf = 0;
    if (!B || !root) return;
    var st = B.getState(); var files = st.files || [];
    trackFiles(files);
    replayRestores();
    var seen = []; var anyGated = null;
    files.forEach(function (f) {
      var g = gateFor(f) || heldGate(f); if (g && !anyGated) anyGated = g;
      var hosts = root.querySelectorAll('[data-bsv4-bgr-for="' + f.id + '"],[data-bsv4-edit-for="' + f.id + '"],[data-bsv4-tile="' + f.id + '"]');
      Array.prototype.forEach.call(hosts, function (host) { seen.push(host); if (g) clearNotes(host, false); each(host, function (el) { mark(el, g || prGate(f, el)); }); });
      if (gateFor(f)) switchOffSettled(f.id);
      else if (reenable[f.id] && !heldGate(f)) reenableSettled(f.id);
    });
    // The phone dock moves the stage toolbar OUT of its host (edit.js dockStageTools); it shows the ACTIVE file.
    var act = activeFile(st); var ag = gateFor(act) || heldGate(act);
    Array.prototype.forEach.call(root.querySelectorAll('.bsv4-aitools--stage'), function (card) {
      if (seen.some(function (h) { return h.contains(card); })) return;
      each(card, function (el) { mark(el, ag || prGate(act, el)); });
    });
    // The DPI warning's "Upscale for Free" button (sizing row, sizing.js) is a second door into
    // Upscale: it follows the active file's gate too.
    Array.prototype.forEach.call(root.querySelectorAll('.bsv4-dpi-up'), function (el) { mark(el, ag || prGate(act, el)); });
    // v5 page-level doors (the stage's Undo/Redo, the Edge row, Add a Note) live outside the per-file hosts; they
    // follow the active file's gate. document, not root: Add a Note can render outside the by-size root.
    if (isV5()) Array.prototype.forEach.call(document.querySelectorAll('[data-ntst-undo],[data-ntst-redo],[data-bsv4-edges-open],[data-ntre-open]'), function (el) {
      if (seen.some(function (h) { return h.contains(el); })) return;
      mark(el, ag);
    });
    // Design Notes on the page (band under the stage, stage tool, navy/rail popover buttons) are
    // page-level, not per file: they follow the active file's gate, like the docked toolbar.
    if (ag) clearNotes(root, true);
    else restoreNotes();
    Array.prototype.forEach.call(root.querySelectorAll('.bsv4-cta__notes, .bsv4-notes-fab'), function (el) {
      if (seen.some(function (h) { return h.contains(el); })) return;
      mark(el, ag);
    });
    // Precut is NOT gated (Owen 2026-10-15: "enable precut on oversized prints") — it stays available at every size.
    void anyGated;
  }
  // setTimeout, not requestAnimationFrame: browsers pause rAF in a hidden tab, and an upload
  // that finishes while the customer is on another tab must still land gated when they return
  // (seen on the sandbox 2026-10-15: hosts rendered, nothing marked while the tab was hidden).
  function schedule() { if (!raf) raf = setTimeout(apply, 0); }

  // ---- tooltip: one real element, positioned above the control --------------------------------
  function ensureTip() {
    if (tip) return tip;
    tip = document.createElement('div');
    tip.className = 'bsv4-size-gate__tip';
    tip.setAttribute('role', 'status');
    document.body.appendChild(tip);
    return tip;
  }
  function showTip(el, sticky) {
    var t = ensureTip();
    t.textContent = el.getAttribute('data-bsv4-size-gate-msg') || '';
    t.classList.add('is-on');
    var r = el.getBoundingClientRect();
    t.style.left = '0px'; t.style.top = '0px';
    var tw = t.offsetWidth, th = t.offsetHeight;
    var left = Math.max(8, Math.min(window.innerWidth - tw - 8, r.left + r.width / 2 - tw / 2));
    var top = r.top - th - 8; if (top < 8) top = r.bottom + 8;
    t.style.left = left + 'px'; t.style.top = top + 'px';
    clearTimeout(tipTimer);
    if (sticky) tipTimer = setTimeout(hideTip, 2800);
  }
  function hideTip() { clearTimeout(tipTimer); if (tip) tip.classList.remove('is-on'); }

  function gated(target) { return target && target.closest ? target.closest('.' + CLS) : null; }
  ['pointerdown', 'click', 'change', 'keydown'].forEach(function (type) {
    window.addEventListener(type, function (e) {
      var g = gated(e.target); if (!g) return;
      if (type === 'keydown' && e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault(); e.stopImmediatePropagation();
      if (type !== 'change') showTip(g, true);
    }, true);
  });
  // v5: Cmd/Ctrl+Z (and Shift+Cmd+Z / Ctrl+Y) is the keyboard door into Undo/Redo — closed while the active file is
  // over the line, like the buttons (window capture runs before nt-stage-tools' document listener).
  window.addEventListener('keydown', function (e) {
    if (!isV5() || !B || !(e.metaKey || e.ctrlKey) || e.altKey) return;
    var k = (e.key || '').toLowerCase(); if (k !== 'z' && k !== 'y') return;
    var a = document.activeElement;
    if (a && (/^(input|textarea|select)$/i.test(a.tagName) || a.isContentEditable)) return;
    try { var af = activeFile(B.getState()); if (!gateFor(af) && !heldGate(af)) return; } catch (x) { return; }
    e.preventDefault(); e.stopImmediatePropagation();
  }, true);
  document.addEventListener('mouseover', function (e) { var g = gated(e.target); if (g) showTip(g, false); }, true);
  document.addEventListener('mouseout', function (e) { var g = gated(e.target); if (g && !(e.relatedTarget && g.contains(e.relatedTarget))) hideTip(); }, true);
  window.addEventListener('scroll', hideTip, true);

  // ---- boot: wait for the engine bridge (the fork loads its bundle asynchronously) ------------
  var tries = 0;
  (function boot() {
    B = window.__bsv4Bridge;
    if (!B || !B.cfg || !B.subscribe) { if (++tries < 400) return setTimeout(boot, 50); return; }
    cfg = B.cfg;
    root = document.querySelector('[data-bsv4-root], .bsv4') || document.body;
    B.subscribe(schedule);
    // controls render after the state commit that announces the file — catch those paints too
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    schedule();
  })();
})();
